/**
 * Le tracé de découpe (CutContour) d'une étiquette : le générer, le mesurer, le
 * poser dans un document.
 *
 * Un seul module décide de ce qu'est une découpe : le menu, l'inspecteur, la
 * création d'un document et l'agent passent tous par ici.
 *
 * Le calque produit a EXACTEMENT la forme de ceux des gabarits de l'atelier
 * (« Découpe · CutContour » dans le groupe « Découpe ») : une forme `svg` non
 * remplie, `isCutContour`, son tracé en POINTS dans un `viewBox`, posée sur une
 * boîte en mm. C'est ce que l'export prêt-presse sait déjà convertir en ton
 * direct vectoriel (`ExportDialog` → `backend/app/print_pro.py`), et ce que
 * l'import PDF produit : un seul format de découpe dans tout LabelForge.
 *
 * Mesuré le 2026-10-12 sur les 7 gabarits de Louise : tracé = format fini au
 * centième de mm, rayon des coins 1,500 mm FIXE (de 1,63 à 4,84 % du petit côté
 * selon le format : ce n'est pas un pourcentage), arcs en vrais quarts de
 * cercle, trait de 0,51 pt.
 */
import { ELEMENT_BASE_DEFAULTS, orderedLeaves } from './layerTree';

/** Nom du ton direct, celui qu'attendent les logiciels de découpe. */
export const DECOUPE_TON_DIRECT = 'CutContour';
/** Épaisseur du trait des gabarits de l'atelier, en points. */
export const DECOUPE_TRAIT_PT = 0.51;
/** Rayon des coins des gabarits de l'atelier, en mm. */
export const DECOUPE_RAYON_DEFAUT_MM = 1.5;
/** Pas de réglage du rayon, en mm. */
export const DECOUPE_PAS_MM = 0.1;
/** Fond perdu d'une étiquette à imprimer, PAR CÔTÉ (3 mm au total). */
export const FOND_PERDU_DEFAUT_MM = 1.5;

/**
 * Les encres possibles du ton direct, à 100 %. `cmjn` est la teinte de
 * remplacement de la séparation (ce que voit un logiciel qui ignore le nom) ;
 * `ecran` la couleur du trait dans l'éditeur. Le magenta est celui des gabarits.
 */
export const ENCRES_DECOUPE = {
    C: { nom: 'Cyan', cmjn: [1, 0, 0, 0], ecran: '#00AEEF' },
    M: { nom: 'Magenta', cmjn: [0, 1, 0, 0], ecran: '#EC008C' },
    J: { nom: 'Jaune', cmjn: [0, 0, 1, 0], ecran: '#FFF200' },
    N: { nom: 'Noir', cmjn: [0, 0, 0, 1], ecran: '#000000' },
};
export const ENCRE_DEFAUT = 'M';

const PT_PAR_MM = 72 / 25.4;
/** Un quart de cercle en Bézier cubique : 4/3·(√2 − 1). */
const K_ARC = 0.5522847498;

/** Une erreur de réglage, avec un code que la couche de commandes reprend tel quel. */
function erreur(message) {
    const e = new Error(message);
    e.code = 'ARGUMENT_INVALIDE';
    return e;
}

const auDixieme = (v) => Math.round(v * 10) / 10;
const nombre = (v) => Number(v.toFixed(3));

/** Le plus grand rayon possible : la moitié du petit côté (au-delà, les arcs se croisent). */
export function rayonMaxMm(largeur_mm, hauteur_mm) {
    return Math.min(largeur_mm, hauteur_mm) / 2;
}

/**
 * Vérifie et normalise les réglages d'une découpe pour un format donné.
 * Rayon arrondi au pas de 0,1 mm ; « carrés » vaut un rayon nul.
 * @returns {{coins: 'carres'|'arrondis', rayon_mm: number, encre: string}}
 */
export function reglagesDecoupe(canvas, { coins = 'arrondis', rayon_mm = DECOUPE_RAYON_DEFAUT_MM, encre = ENCRE_DEFAUT } = {}) {
    const L = Number(canvas?.width_mm), H = Number(canvas?.height_mm);
    if (!(L > 0) || !(H > 0)) throw erreur('Le format du document (largeur et hauteur en mm) est requis pour tracer la découpe.');
    if (!['carres', 'arrondis'].includes(coins)) throw erreur('« coins » vaut « carres » ou « arrondis ».');
    if (!ENCRES_DECOUPE[encre]) throw erreur(`« encre » vaut ${Object.keys(ENCRES_DECOUPE).join(', ')} (cyan, magenta, jaune ou noir, à 100 %).`);
    if (coins === 'carres') return { coins, rayon_mm: 0, encre };
    const r = Number(rayon_mm);
    if (!Number.isFinite(r) || r < 0) throw erreur('Le rayon des coins est un nombre de mm, positif ou nul.');
    const rayon = auDixieme(r);
    const max = rayonMaxMm(L, H);
    if (rayon > max) throw erreur(`Rayon de ${rayon} mm trop grand pour un format de ${L} × ${H} mm : au plus ${auDixieme(Math.floor(max * 10) / 10)} mm.`);
    return rayon === 0 ? { coins: 'carres', rayon_mm: 0, encre } : { coins, rayon_mm: rayon, encre };
}

/** Le tracé `d` d'un rectangle de L × H points, à coins arrondis de rayon r (points). */
export function cheminDecoupe(L, H, r) {
    if (r <= 0) return `M0 0 L${nombre(L)} 0 L${nombre(L)} ${nombre(H)} L0 ${nombre(H)} Z`;
    const k = r * K_ARC;
    const p = (...v) => v.map(nombre).join(' ');
    return [
        `M${p(r, 0)}`,
        `L${p(L - r, 0)}`, `C${p(L - r + k, 0, L, r - k, L, r)}`,
        `L${p(L, H - r)}`, `C${p(L, H - r + k, L - r + k, H, L - r, H)}`,
        `L${p(r, H)}`, `C${p(r - k, H, 0, H - r + k, 0, H - r)}`,
        `L${p(0, r)}`, `C${p(0, r - k, r - k, 0, r, 0)}`,
        'Z',
    ].join(' ');
}

/**
 * Le groupe « Découpe » et son calque « Découpe · CutContour » pour un format.
 * Le calque suit le format fini au millimètre (le fond perdu reste dehors), il
 * est VERROUILLÉ (on le déverrouille dans le panneau des calques) et retient ses
 * réglages dans `decoupe`, pour être régénéré si le format change.
 */
export function genererDecoupe(canvas, options, nouvelId) {
    const r = reglagesDecoupe(canvas, options);
    const L = Number(canvas.width_mm), H = Number(canvas.height_mm);
    const Lpt = L * PT_PAR_MM, Hpt = H * PT_PAR_MM;
    const base = () => JSON.parse(JSON.stringify(ELEMENT_BASE_DEFAULTS));
    const groupe = {
        ...base(), id: nouvelId(), type: 'group', name: 'Découpe',
        isExpanded: true, parentId: null, layerColor: '#94a3b8',
    };
    const calque = {
        ...base(), id: nouvelId(), parentId: groupe.id,
        type: 'shape', shapeType: 'svg', name: `Découpe · ${DECOUPE_TON_DIRECT}`,
        x: 0, y: 0, width: L, height: H, rotation: 0, opacity: 100,
        svgPath: cheminDecoupe(Lpt, Hpt, r.rayon_mm * PT_PAR_MM),
        svgViewBox: `0 0 ${nombre(Lpt)} ${nombre(Hpt)}`,
        svgFill: false, strokeWidth: DECOUPE_TRAIT_PT,
        color: ENCRES_DECOUPE[r.encre].ecran,
        locked: true, isCutContour: true,
        decoupe: { ...r, auto: true },
    };
    return { groupe, calque };
}

/* ─── Mesurer ───────────────────────────────────────────────────────────── */

const JETON = /([MLCHVZmlchvz])|(-?\d*\.?\d+(?:[eE][-+]?\d+)?)/g;

/** Les segments d'un tracé en commandes ABSOLUES (celles de l'import et du générateur). */
function segments(d) {
    const seq = [...String(d).matchAll(JETON)].map((m) => (m[1] ? m[1] : Number(m[2])));
    const out = [];
    let cmd = null, cur = [0, 0], debut = [0, 0];
    for (let i = 0; i < seq.length;) {
        if (typeof seq[i] === 'string') {
            cmd = seq[i].toUpperCase(); i++;
            if (cmd === 'Z') { out.push({ type: 'L', de: cur, a: debut }); cur = debut; }
            continue;
        }
        if (cmd === 'M') { cur = debut = [seq[i], seq[i + 1]]; i += 2; cmd = 'L'; continue; }
        if (cmd === 'L') { const a = [seq[i], seq[i + 1]]; out.push({ type: 'L', de: cur, a }); cur = a; i += 2; continue; }
        if (cmd === 'H') { const a = [seq[i], cur[1]]; out.push({ type: 'L', de: cur, a }); cur = a; i += 1; continue; }
        if (cmd === 'V') { const a = [cur[0], seq[i]]; out.push({ type: 'L', de: cur, a }); cur = a; i += 1; continue; }
        if (cmd === 'C') { const a = [seq[i + 4], seq[i + 5]]; out.push({ type: 'C', de: cur, a }); cur = a; i += 6; continue; }
        i++;   // commande non gérée : on saute la valeur
    }
    return out;
}

/**
 * Mesure un calque de découpe : sa place et sa taille en mm du document, et
 * l'arrondi de ses coins. Marche sur les tracés de l'atelier, de l'import et du
 * générateur. `null` si le calque n'a pas de tracé lisible.
 * @returns {null|{x_mm, y_mm, largeur_mm, hauteur_mm, coins: 'carres'|'arrondis'|'irreguliers', rayon_mm: number|null, rayons_mm: number[]}}
 */
export function mesurerDecoupe(el) {
    if (!el?.svgPath || !el?.svgViewBox) return null;
    const [vx, vy, vw, vh] = String(el.svgViewBox).trim().split(/[\s,]+/).map(Number);
    if (!(vw > 0) || !(vh > 0)) return null;
    const sx = el.width / vw, sy = el.height / vh;          // mm par unité du viewBox
    const segs = segments(el.svgPath).filter((s) => s.de[0] !== s.a[0] || s.de[1] !== s.a[1]);
    if (segs.length === 0) return null;
    const xs = segs.flatMap((s) => [s.de[0], s.a[0]]), ys = segs.flatMap((s) => [s.de[1], s.a[1]]);
    const mmX = (px) => el.x + (px - vx) * sx, mmY = (py) => el.y + (py - vy) * sy;
    const x0 = mmX(Math.min(...xs)), x1 = mmX(Math.max(...xs));
    const y0 = mmY(Math.min(...ys)), y1 = mmY(Math.max(...ys));
    // Le rayon d'un coin : l'écart entre le départ et l'arrivée de son arc, sur
    // chaque axe. Un vrai quart de cercle donne le même écart sur les deux ; un
    // ovale, non (60 et 39 mm sur un 120 × 78), et passait pour un rectangle
    // arrondi de 49,5 mm de rayon. Écart mesuré sur les 7 gabarits de l'atelier :
    // 0,0004 mm au plus, sous la tolérance de 0,01 mm.
    const quarts = segs.filter((s) => s.type === 'C').map((s) => [Math.abs(s.a[0] - s.de[0]) * sx, Math.abs(s.a[1] - s.de[1]) * sy]);
    const arcs = quarts.map(([dx, dy]) => (dx + dy) / 2);
    const rond = (v) => Math.round(v * 1000) / 1000;
    const rayons = arcs.map(rond);
    let coins = 'irreguliers', rayon = null;
    if (arcs.length === 0 && segs.length === 4) { coins = 'carres'; rayon = 0; }
    else if (arcs.length === 4 && quarts.every(([dx, dy]) => Math.abs(dx - dy) < 0.01) && Math.max(...arcs) - Math.min(...arcs) < 0.01) {
        coins = 'arrondis'; rayon = rond(arcs.reduce((a, b) => a + b, 0) / 4);
    }
    return { x_mm: rond(x0), y_mm: rond(y0), largeur_mm: rond(x1 - x0), hauteur_mm: rond(y1 - y0), coins, rayon_mm: rayon, rayons_mm: rayons };
}

/** Un calque est-il un tracé de découpe ? */
export const estDecoupe = (el) => !!el?.isCutContour;

/* ─── Poser dans un document ────────────────────────────────────────────── */

/**
 * Pose une découpe neuve dans un document, AU PREMIER PLAN : le groupe « Découpe »
 * part en dernier dans le tableau (peint par-dessus tout, en haut du panneau des
 * calques). Les découpes existantes sont remplacées, et un groupe « Découpe »
 * qu'elles laissent vide disparaît avec elles.
 * @returns {{elements: object[], calqueId: string, remplaces: number}}
 */
export function poserDecoupe(elements, canvas, options, nouvelId) {
    const { groupe, calque } = genererDecoupe(canvas, options, nouvelId);
    const anciennes = new Set((elements || []).filter(estDecoupe).map((e) => e.id));
    const restants = (elements || []).filter((e) => !anciennes.has(e.id));
    const parents = new Set(restants.map((e) => e.parentId).filter(Boolean));
    const vides = new Set(restants.filter((e) => e.type === 'group' && /^découpe/i.test(e.name || '') && !parents.has(e.id)).map((e) => e.id));
    return {
        elements: [...restants.filter((e) => !vides.has(e.id)), groupe, calque],
        calqueId: calque.id,
        remplaces: anciennes.size,
    };
}

/** La découpe est-elle le calque peint en dernier (au premier plan) ? */
export function estAuPremierPlan(elements, id) {
    const feuilles = orderedLeaves(elements || []).filter((x) => !x.effHidden);
    return feuilles.length > 0 && feuilles[feuilles.length - 1].el.id === id;
}

/**
 * Remonte une découpe au premier plan, SEULE. Les gabarits de l'atelier la rangent
 * près du bas, dans un groupe « Découpe » qui contient parfois une forme du visuel
 * (« Forme 3 (copie) » dans le 100 ml) : remonter tout le groupe ferait passer
 * cette forme par-dessus l'étiquette. Le tracé quitte donc son groupe s'il n'y est
 * pas seul, pour un groupe « Découpe » neuf, placé en dernier ; un groupe qui ne
 * contenait que lui part avec lui.
 */
export function decoupeAuPremierPlan(elements, id, nouvelId) {
    const els = elements || [];
    const cut = els.find((e) => e.id === id);
    if (!cut) return els;
    const parent = cut.parentId ? els.find((e) => e.id === cut.parentId) : null;
    const seulDansSonGroupe = parent && els.filter((e) => e.parentId === parent.id).length === 1;
    if (seulDansSonGroupe && !parent.parentId) {
        return [...els.filter((e) => e.id !== parent.id && e.id !== cut.id), parent, cut];
    }
    const groupe = {
        ...JSON.parse(JSON.stringify(ELEMENT_BASE_DEFAULTS)), id: nouvelId(), type: 'group',
        name: 'Découpe', isExpanded: true, parentId: null, layerColor: '#94a3b8',
    };
    const restants = els.filter((e) => e.id !== cut.id && !(seulDansSonGroupe && e.id === parent.id));
    return [...restants, groupe, { ...cut, parentId: groupe.id }];
}

/**
 * Un document NEUF reçoit sa découpe (décision du 2026-10-12 : toute étiquette
 * créée dans LabelForge, à la main ou par l'agent, porte son CutContour). Rien
 * n'est ajouté s'il en a déjà une (gabarit de l'atelier, PDF importé), ni si le
 * format est illisible : la création d'un document ne doit jamais échouer pour ça.
 * @returns {object[]} les calques, avec la découpe au premier plan s'il en manquait une
 */
export function avecDecoupe(elements, canvas, nouvelId, options) {
    const els = elements || [];
    if (els.some(estDecoupe)) return els;
    try {
        return poserDecoupe(els, canvas, options, nouvelId).elements;
    } catch {
        return els;
    }
}

/**
 * Un document est-il VIERGE, à son contenu ? Rien d'autre que sa découpe générée
 * (`decoupe.auto`) et le groupe « Découpe » qui la range. Depuis que tout
 * document neuf naît avec sa découpe, « aucun calque » ne reconnaissait plus un
 * onglet neuf : l'import d'un seul PDF laissait un onglet vide en trop. Une
 * découpe importée, ou un groupe « Découpe » qui range autre chose (« Forme 3
 * (copie) » du 100 ml), font un document qui n'est pas vierge. Le « jamais
 * modifié » se lit à part, sur l'onglet (`isDirty`).
 */
export function estDocumentVierge(elements) {
    const els = elements || [];
    const generees = new Set(els.filter((e) => estDecoupe(e) && e.decoupe?.auto).map((e) => e.id));
    const rangeQueDesGenerees = (g) => /^découpe/i.test(g.name || '')
        && els.every((x) => x.parentId !== g.id || generees.has(x.id));
    return els.every((e) => generees.has(e.id) || (e.type === 'group' && rangeQueDesGenerees(e)));
}

/**
 * Après un changement de format : régénère les découpes GÉNÉRÉES (`decoupe.auto`)
 * à la nouvelle taille, avec leurs réglages, leur verrou et leur place dans la
 * pile. Une découpe importée ou tracée à la main n'est jamais touchée.
 *
 * Un rayon trop grand pour le nouveau format est BORNÉ dans le tracé seulement :
 * le réglage demandé (`decoupe.rayon_mm`) est gardé. Sans quoi la saisie d'un
 * format dans les Préférences, qui passe par « 1 » avant « 120 », ramenait le
 * rayon à 0,5 mm et l'y laissait pour de bon.
 * @returns {{elements: object[], regeneres: number}}
 */
export function regenererDecoupesAuto(elements, canvas, nouvelId) {
    let regeneres = 0;
    const out = (elements || []).map((el) => {
        if (!estDecoupe(el) || !el.decoupe?.auto) return el;
        const { calque } = genererDecoupe(canvas, { ...el.decoupe, rayon_mm: rayonTrace(el.decoupe, canvas) }, nouvelId);
        if (calque.svgPath === el.svgPath && calque.svgViewBox === el.svgViewBox && calque.width === el.width && calque.height === el.height) return el;
        regeneres++;
        return { ...el, ...pick(calque, ['x', 'y', 'width', 'height', 'svgPath', 'svgViewBox']) };
    });
    return { elements: out, regeneres };
}

/** Le rayon réellement tracé pour un réglage : le rayon demandé, borné par le format. */
export function rayonTrace(reglage, canvas) {
    if (reglage?.coins === 'carres') return 0;
    const max = Math.floor(rayonMaxMm(canvas.width_mm, canvas.height_mm) * 10) / 10;
    return Math.min(Number(reglage?.rayon_mm ?? DECOUPE_RAYON_DEFAUT_MM), max);
}

function pick(o, cles) {
    return Object.fromEntries(cles.map((k) => [k, o[k]]));
}
